import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'next-i18next';

import { LtiClient } from '@/shared/lti/LtiClient';

type SelectionStatus = 'idle' | 'loading' | 'success' | 'error';

interface LtiSelectParams {
  quizSetId: string;
  entryMapHashId: string;
  quizTitle: string;
}

const SUCCESS_DISPLAY_DELAY_MS = 500;
/**
 * Grace period after triggering the LMS form submission before allowing retry.
 * If navigation happens the document tears down and this timer never matters.
 * If navigation is blocked (popup blocker, LMS rejected JWT, etc.) the user
 * would otherwise be stuck in the "success" state forever.
 */
const SUCCESS_RETRY_FALLBACK_MS = 5000;
const ERROR_RETRY_FALLBACK_MS = 2000;

export function useLtiSelectAction() {
  const [status, setStatus] = useState<SelectionStatus>('idle');
  const { t } = useTranslation(['common']);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleReset = useCallback((ms: number) => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => setStatus('idle'), ms);
  }, []);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const selectQuiz = useCallback(
    async (params: LtiSelectParams) => {
      if (status === 'loading' || status === 'success') return;

      setStatus('loading');

      try {
        const { jwt, returnUrl } = await LtiClient.submitDeepLinkingSelection(
          params,
        );

        setStatus('success');

        // Brief delay to show success state before redirect
        await new Promise(resolve =>
          setTimeout(resolve, SUCCESS_DISPLAY_DELAY_MS),
        );

        LtiClient.submitDeepLinkingForm(returnUrl, jwt);

        scheduleReset(SUCCESS_RETRY_FALLBACK_MS);
      } catch (err) {
        setStatus('error');
        console.error('[LTI] Deep linking failed:', err);
        toast.error(t('lti.error.selectFailed'));

        scheduleReset(ERROR_RETRY_FALLBACK_MS);
      }
    },
    [status, t, scheduleReset],
  );

  return { selectQuiz, status };
}
